/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  DropdownOption,
  DropdownOption$inboundSchema,
  DropdownOption$Outbound,
  DropdownOption$outboundSchema,
} from "./dropdownoption.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The type of display element to use for the reading or setting.
 */
export const DisplayRefDisplayType = {
  Label: "label",
  Dropdown: "dropdown",
  Input: "input",
  Button: "button",
  Switch: "switch",
} as const;
/**
 * The type of display element to use for the reading or setting.
 */
export type DisplayRefDisplayType = OpenEnum<typeof DisplayRefDisplayType>;

/**
 * A reading or setting reference that is used to display a value.
 */
export type DisplayRef = {
  /**
   * The description of the reading or setting.
   */
  description?: string | undefined;
  /**
   * If display_type is dropdown, this is the set of options to display for the dropdown.
   */
  displayOptions?: Array<DropdownOption> | undefined;
  /**
   * The type of display element to use for the reading or setting.
   */
  displayType?: DisplayRefDisplayType | undefined;
  /**
   * The display units of the reading or setting. The desired display units may differ from the actual units.  For example: time in seconds might be desired to displayed in minutes.
   */
  displayUnits?: string | undefined;
  /**
   * The label of the reading or setting.
   */
  label: string;
  /**
   * A reference to a reading or setting.
   */
  ref: Ref;
};

/** @internal */
export const DisplayRefDisplayType$inboundSchema: z.ZodType<
  DisplayRefDisplayType,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(DisplayRefDisplayType);
/** @internal */
export const DisplayRefDisplayType$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  DisplayRefDisplayType
> = openEnums.outboundSchema(DisplayRefDisplayType);

/** @internal */
export const DisplayRef$inboundSchema: z.ZodType<
  DisplayRef,
  z.ZodTypeDef,
  unknown
> = z.object({
  description: types.optional(types.string()),
  display_options: types.optional(z.array(DropdownOption$inboundSchema)),
  display_type: DisplayRefDisplayType$inboundSchema.default("label"),
  display_units: types.optional(types.string()),
  label: types.string(),
  ref: Ref$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "display_options": "displayOptions",
    "display_type": "displayType",
    "display_units": "displayUnits",
  });
});
/** @internal */
export type DisplayRef$Outbound = {
  description?: string | undefined;
  display_options?: Array<DropdownOption$Outbound> | undefined;
  display_type: string;
  display_units?: string | undefined;
  label: string;
  ref: Ref$Outbound;
};

/** @internal */
export const DisplayRef$outboundSchema: z.ZodType<
  DisplayRef$Outbound,
  z.ZodTypeDef,
  DisplayRef
> = z.object({
  description: z.string().optional(),
  displayOptions: z.array(DropdownOption$outboundSchema).optional(),
  displayType: DisplayRefDisplayType$outboundSchema.default("label"),
  displayUnits: z.string().optional(),
  label: z.string(),
  ref: Ref$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    displayOptions: "display_options",
    displayType: "display_type",
    displayUnits: "display_units",
  });
});

export function displayRefToJSON(displayRef: DisplayRef): string {
  return JSON.stringify(DisplayRef$outboundSchema.parse(displayRef));
}
export function displayRefFromJSON(
  jsonString: string,
): SafeParseResult<DisplayRef, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DisplayRef$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DisplayRef' from JSON`,
  );
}
